import {Component, computed, effect, inject, input, signal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {TechnicalSecuritySystemModel} from "../_models/technical-security-system-model";
import copy from "copy-to-clipboard";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {AlertLevel} from "../../../enums/alert-level";
import {KassenSichVState, KassenSichVStateHelper} from "../_models/_enums/kassensichv-state";
import {TechnicalSecuritySystemHttpService} from "../_services/technical-security-system-http.service";
import {UnixEpochTimePipe} from "../_helper/unix-epoch-time.pipe";
import {
  describeLoadError,
  describeReinitializeError,
  isResumableReinitializeError,
  readErrorCode,
  readErrorDetails
} from "../_helper/legal-tss-errors";
import {
  LegalTssReinitializeStep,
  LegalTssReinitializeStepHelper
} from "../_models/_enums/legal-tss-reinitialize-step";
import {
  RetiredTechnicalSecuritySystemReasonHelper
} from "../_models/_enums/retired-technical-security-system-reason";
import {TssReinitializeConfirmDialogComponent} from "./tss-reinitialize-confirm-dialog.component";
import {LegalReadonlyHintComponent} from "../_shared/legal-readonly-hint.component";

/** The card's own view of a failed `GetCurrent`/`ReInitialize` call — never carries `ApiError.description`. */
interface LegalTssErrorView {
  message: string;
  code: string | null;
  /** Only set for a `ReInitialize` refusal that offers a "Fortsetzen" action. */
  resumable: boolean;
  /** Only set for a `ReInitialize` refusal whose `details.step` names where the attempt stopped. */
  step?: LegalTssReinitializeStep;
}

@Component({
  selector: 'app-technical-security-system',
  templateUrl: './technical-security-system.component.html',
  imports: [
    DatePipe,
    UnixEpochTimePipe,
    TssReinitializeConfirmDialogComponent,
    LegalReadonlyHintComponent
  ],
  styleUrls: ['./technical-security-system.component.scss']
})
export class TechnicalSecuritySystemComponent {
  private readonly globalAlertService = inject(GlobalAlertService);
  private readonly technicalSecuritySystemHttpService = inject(TechnicalSecuritySystemHttpService);

  protected readonly Object = Object;
  protected readonly KassenSichVStateHelper = KassenSichVStateHelper;
  protected readonly KassenSichVState = KassenSichVState;
  protected readonly RetiredTechnicalSecuritySystemReasonHelper = RetiredTechnicalSecuritySystemReasonHelper;
  protected readonly LegalTssReinitializeStepHelper = LegalTssReinitializeStepHelper;

  /**
   * (#633) Set only when this instance is embedded in the OWNER area for a specific shard (`owner/shard/…`'s
   * "Rechtliches" step) — never on the tenant route `/legal/tss`. A `PlatformOwner` who has entered a tenant
   * context loses the `PlatformOwner` role by design (context narrowing), so a permission check here could
   * never be reached from the tenant route in the first place; the owner must always come through the OWNER
   * area instead, where the shard id is explicit. `PermissionService` is deliberately no longer a dependency
   * of this component. See `SettingsComponent`'s identical `shardId`/`isOwner` pair.
   */
  readonly shardId = input<string | undefined>();

  /**
   * OWNER MODE (full mutating controls, HTTP calls scoped to {@link shardId}) vs TENANT MODE (read-only for
   * everyone, incl. a PlatformOwner viewing a shard's `/legal/tss` — see the class doc on {@link shardId}).
   */
  protected readonly isOwner = computed(() => this.shardId() !== undefined);

  protected readonly current = signal<TechnicalSecuritySystemModel | undefined>(undefined);

  /**
   * Whether the BSI certification's expiry (`bsiCertificationValidTo`, a Unix epoch **second**) is already in
   * the past — what puts „abgelaufen" beside the date in the „Informationen" tile.
   *
   * `false` for an unanswered expiry (`0`), and that is the point rather than a side effect: the template never
   * reaches this branch for a `0` because it renders no row at all, and a truthy answer here would mean the
   * screen calling a certification expired on the strength of a value the provider never gave it.
   *
   * `Date.now()` is read inside a `computed`, so it is sampled when `current()` changes rather than ticking —
   * deliberate: a certificate horizon is months or years away, nothing on this page would be improved by the
   * word appearing mid-session, and a timer would re-render a legally load-bearing screen for no reader.
   */
  protected readonly bsiCertificationExpired = computed(() => {
    const validTo = this.current()?.bsiCertificationValidTo ?? 0;
    return validTo > 0 && validTo * 1000 < Date.now();
  });

  /**
   * The `GetCurrent` refusal, if the last load failed. Deliberately does NOT clear {@link current} on error —
   * the page stays rendered with whatever it last knew, plus this banner, instead of the historic "swallow
   * the error, show nothing" behaviour (#613 finding).
   */
  protected readonly loadError = signal<LegalTssErrorView | undefined>(undefined);

  protected readonly reinitializeDialogOpen = signal(false);
  protected readonly reinitializing = signal(false);
  protected readonly reinitializeError = signal<LegalTssErrorView | undefined>(undefined);

  constructor() {
    // Load in an EFFECT, not in `ngOnInit` (#631): **Owner → Rechtliches** keeps this component mounted and
    // swaps the picked shard, so a once-only `ngOnInit` load would leave one location's TSE on screen under
    // another location's heading — and the state buttons underneath it would then act on the new shard while
    // showing the old one's state. Re-reading on every `shardId` change is the only safe contract for a
    // screen whose actions retire a legal signing device.
    effect(() => {
      this.shardId();
      // A re-initialisation refusal belongs to the shard it was raised on — carrying its "Fortsetzen" over
      // to the next shard would offer to resume a job that shard never started. Cleared HERE and not in
      // `load()`, because `load()` is also the banner's own "Erneut laden" button, and clearing it there
      // would take away the only control that can finish an interrupted re-initialisation.
      this.reinitializeError.set(undefined);
      this.load();
    });
  }

  copyInformation(information: string) {
    copy(information)
    this.globalAlertService.createAndShow("Hinweis", "Information wurde in der Zwischenablage gesichert.", AlertLevel.warning, 5000);
  }

  load() {
    this.loadError.set(undefined);
    this.technicalSecuritySystemHttpService.getCurrent(this.shardId())
      .withLoadingIndicator()
      .subscribe({
        next: (value) => {
          this.current.set(value);
        },
        error: (error) => {
          // The persistent banner (describeLoadError + code) is the single message for a failed load; a toast on
          // top of it said the same thing twice in different words.
          this.loadError.set({message: describeLoadError(), code: readErrorCode(error), resumable: false});
        }
      })
  }

  changeState(state: KassenSichVState) {
    // Defence in depth: the template only renders the state buttons in owner mode, but the method itself must
    // also refuse — matches `SettingsComponent`'s guarded mutators (#613/#633).
    if (!this.isOwner()) return;
    const current = this.current();
    if (!current || !KassenSichVStateHelper.canTransitionTo(current.state, state)) return;

    this.technicalSecuritySystemHttpService.changeState(state, this.shardId())
      .withLoadingIndicator()
      .subscribe({
        next: (value) => {
          this.current.set(value);
          this.globalAlertService.createAndShow("Erfolg", "Status der Technischen Sicherheitseinrichtung wurde geändert.", AlertLevel.success, 5000);
        },
        error: (error) => {
          this.globalAlertService.createAndShow("Fehler", "Fehler beim Ändern des Status der technischen Sicherheitseinrichtung.", AlertLevel.error, 5000);
        }
      })
  }

  protected openReinitializeDialog(): void {
    if (!this.isOwner()) return;
    this.reinitializeDialogOpen.set(true);
  }

  protected onReinitializeDialogCancelled(): void {
    this.reinitializeDialogOpen.set(false);
  }

  protected onReinitializeDialogConfirmed(): void {
    this.reinitializeDialogOpen.set(false);
    this.runReinitialize();
  }

  /** The "Fortsetzen" action on a `RESUME_AT_*` refusal — no dialog: the reader already confirmed once. */
  protected resumeReinitialize(): void {
    this.runReinitialize();
  }

  private runReinitialize(): void {
    // Defence in depth — the single chokepoint both the dialog-confirm and "Fortsetzen" paths funnel through.
    if (!this.isOwner()) return;
    this.reinitializing.set(true);
    this.reinitializeError.set(undefined);

    this.technicalSecuritySystemHttpService.reInitialize(this.shardId())
      .subscribe({
        next: (result) => {
          this.reinitializing.set(false);
          this.loadError.set(undefined);
          // Fold the one-time PUK into `current` so the existing PUK card renders it immediately — see the
          // class doc on why this stays the single source for that banner.
          this.current.set(result.adminPuk ? {...result.tss, adminPuk: result.adminPuk} : result.tss);
          this.globalAlertService.createAndShow("Erfolg",
            "Die technische Sicherheitseinrichtung wurde neu initialisiert.", AlertLevel.success, 5000);
        },
        error: (error) => {
          this.reinitializing.set(false);
          const details = readErrorDetails(error);
          if (details?.tss) this.current.set(details.tss);

          this.reinitializeError.set({
            message: describeReinitializeError(error),
            code: readErrorCode(error),
            resumable: isResumableReinitializeError(error),
            step: details?.step
          });
        }
      });
  }
}
